// Row-content translation memory. File-mode rows already translated by
// THESE exact weights come back without touching the GPU — the payoff is
// the webnovel update workflow: packing is paragraph-local (packSentences
// never merges across paragraph breaks), so when a novel grows by new
// chapters the old rows re-pack byte-identically and only the new tail
// decodes (measured on 青山.txt: an 80%→100% re-drop serves 80.02% of
// chars from memory; within-file duplicate rows are noise, <1% of chars).
//
// This SUPERSEDES the old single-slot partial store: content-keyed rows
// are strictly more general than an index-aligned slot (crash/cancel
// resume = the same lookup), so DB version 2 drops the 'partial' store.
//
// Honest framing (same rigor as the shortlist): a hit returns a real
// engine output for that row, but not necessarily the bits a FRESH run
// would produce — f16 near-ties can flip across batch shapes (precedent:
// 1/60,353 rows in the length-sort round). Keys carry the weights content
// tag, so swapping models/weights can never serve the old model's voice.
// `?tm=0` bypasses the whole feature.
//
// Every function swallows errors — private mode / quota loss just turns
// the memory off, it must never break the translation itself.

const DB_NAME = 'moxhi';
const DB_VERSION = 2;
const STORE = 'tm';
const SEP = '\u0000'; // namespaces never contain NUL, so keys can't collide
const DEFAULT_CAP = 150_000; // ≈2 novels of rows, order tens of MB

// Pure: one namespace per (model, weights content, dtype[, tokenizer rev]).
// weightsTag is the first shard name of a deployed manifest (content-hashed,
// e.g. "weights.2dd5731a.p0.bin") or 'dev' for the unsharded local path — dev
// entries can go stale across a local weights swap; production cannot.
// tokRev (model registry) covers the one axis weightsTag misses: a tokenizer
// asset swap with unchanged weights re-tokenizes the same row into different
// ids, so cached rows would keep serving the old output forever. Absent
// tokRev keeps the historical 3-part namespace (no TM loss for models that
// never swapped tokenizers).
export function tmNamespace({ modelId, weightsTag, dtype, tokRev } = {}) {
  const parts = [modelId ?? 'unknown', weightsTag ?? 'dev', dtype ?? 'f16'];
  if (tokRev) parts.push(tokRev);
  return parts.join('|');
}

// Pure: does the last-run marker (localStorage, main.js) describe an
// interrupted run of exactly this file? Same-identity check the old
// partial slot used — the marker is only the pre-click HINT; the actual
// resume fuel is the content-keyed rows, which need no identity at all.
export function markerMatches(saved, cur) {
  return !!saved && !saved.complete && (saved.done ?? 0) > 0
    && saved.file === cur.file && saved.size === cur.size
    && saved.encoding === cur.encoding && saved.model === cur.model;
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE).createIndex('ts', 'ts');
      }
      // v1's single-slot partial store — superseded by the row memory.
      if (db.objectStoreNames.contains('partial')) db.deleteObjectStore('partial');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const out = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(typeof out === 'function' ? out() : out);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('tx aborted'));
    });
  } finally {
    db.close();
  }
}

// Translated text for each row (null where unknown), aligned with `rows`.
// One readonly transaction; ~60k gets on a novel run in about a second —
// noise next to the minutes of GPU work they replace.
export async function tmGet(ns, rows) {
  const out = new Array(rows.length).fill(null);
  try {
    await withStore('readonly', (store) => {
      rows.forEach((row, i) => {
        const req = store.get(ns + SEP + row);
        req.onsuccess = () => { if (req.result) out[i] = req.result.t; };
      });
    });
  } catch { /* memory off — translate everything */ }
  return out;
}

// entries: [sourceRow, translatedText][] — one batch per transaction,
// fire-and-forget from the caller's point of view.
export async function tmPut(ns, entries) {
  try {
    const ts = Date.now();
    await withStore('readwrite', (store) => {
      for (const [row, t] of entries) store.put({ t, ts }, ns + SEP + row);
    });
  } catch { /* memory-only feature */ }
}

// Global LRU cap across all namespaces: drop oldest rows (by write time)
// until 10% under the cap. Called fire-and-forget after a file completes.
export async function tmPrune(cap = DEFAULT_CAP) {
  try {
    const total = await withStore('readonly', (store) => {
      const req = store.count();
      return () => req.result;
    });
    if (total <= cap) return;
    let excess = total - Math.floor(cap * 0.9);
    await withStore('readwrite', (store) => {
      const cur = store.index('ts').openCursor();
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c || excess <= 0) return;
        c.delete();
        excess--;
        c.continue();
      };
    });
  } catch { /* ignore */ }
}

// Inventory for the "Bộ nhớ dịch" panel: what is stored, per namespace.
//
// Rows are counted EXACTLY — one count() over each namespace's key range.
// Bytes are EXTRAPOLATED from a bounded sample, because IndexedDB reports no
// per-record size and reading 150k values back just to sum their lengths
// would cost more than the readout is worth. The panel labels them "≈".
//
// Namespaces are found by skip-scan: land on the first key of a namespace,
// then jump straight past the whole block, so the cost is one cursor hop per
// namespace rather than per row. Keys sort correctly for this even when one
// namespace is a prefix of another (3-part vs 4-part with tokRev), because
// NUL sorts below every character a namespace can contain.
export async function tmStats({ sample = 64 } = {}) {
  try {
    const namespaces = await withStore('readonly', (store) => {
      const found = [];
      const cur = store.openKeyCursor();
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) return;
        const key = String(c.key);
        const i = key.indexOf(SEP);
        if (i < 0) return; // not one of ours — stop rather than risk a loop
        const ns = key.slice(0, i);
        found.push(ns);
        c.continue(ns + '\u0001');
      };
      return () => found;
    });
    if (!namespaces.length) return [];
    return await withStore('readonly', (store) => {
      const out = namespaces.map((ns) => ({ ns, rows: 0, bytes: 0 }));
      namespaces.forEach((ns, i) => {
        const range = IDBKeyRange.bound(ns + SEP, ns + '\u0001', false, true);
        // Requests within a transaction complete in the order they are made,
        // so rows is already set when the sample lands.
        const count = store.count(range);
        count.onsuccess = () => { out[i].rows = count.result; };
        const keys = store.getAllKeys(range, sample);
        const vals = store.getAll(range, sample);
        vals.onsuccess = () => {
          const n = vals.result.length;
          if (!n) return;
          const chars = (keys.result ?? []).reduce((a, k) => a + String(k).length, 0)
            + vals.result.reduce((a, v) => a + (v?.t?.length ?? 0), 0);
          out[i].bytes = Math.round((chars / n) * 2 * out[i].rows); // UTF-16
        };
      });
      return () => out;
    });
  } catch {
    return []; // memory off (private mode / blocked storage) — nothing to show
  }
}

// Drop one namespace entirely (tests; the "Bộ nhớ dịch" panel).
export async function tmClear(ns) {
  try {
    await withStore('readwrite', (store) => {
      // every key in the namespace sits in [ns+NUL, ns+SOH)
      store.delete(IDBKeyRange.bound(ns + '\u0000', ns + '\u0001', false, true));
    });
  } catch { /* ignore */ }
}
